import React, { useState } from 'react';
import { ArrowLeft, Star, MoonStar, Rocket, Globe, ExternalLink } from 'lucide-react';

const SpaceBluesPage = () => {
    const [activeSection, setActiveSection] = useState(0);

    const sections = [
        {
            title: "Cultural Payloads",
            content: "Small modules designed to carry art, sound, or memory—permanently or ephemerally.",
            icon: <Star className="w-6 h-6" />,
        },
        {
            title: "Probes as Presence",
            content: "Rethinking robotic missions as carriers of meaning, not just instruments.",
            icon: <Rocket className="w-6 h-6" />,
        },
        {
            title: "Earth-Based Prototypes",
            content: "Public installations that reconnect people with the night sky and remind us what space is for.",
            icon: <Globe className="w-6 h-6" />,
        },
        {
            title: "Distributed Time Capsules",
            content: "Open frameworks for preserving shared human expression beyond Earth.",
            icon: <MoonStar className="w-6 h-6" />,
        },
    ];

    return (
        <div className="bg-gradient-to-b from-black via-blue-950 to-purple-950 min-h-screen text-white font-mono overflow-hidden relative">
            {/* Animated stars background */}
            <div className="fixed inset-0 z-0">
                {[...Array(50)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-white rounded-full"
                        style={{
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animation: `starMove ${30 + Math.random() * 20}s linear infinite`,
                        }}
                    />
                ))}
            </div>

            {/* Main content container */}
            <div className="relative z-10 max-w-6xl mx-auto px-8 py-12">
                {/* Navigation */}
                <nav className="flex justify-between items-center mb-16">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center space-x-2 hover:text-blue-400 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Exit</span>
                    </button>
                    <div className="text-blue-400">Space Blues Co.</div>
                </nav>

                {/* Hero section */}
                <div className="text-center mb-24">
                    <h1 className="text-6xl mb-8 font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
                        Space Blues Co.
                    </h1>
                    <div className="mb-6 flex justify-center">
                        <a
                            href="https://www.spaceblues.co/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-blue-400/40 bg-white/5 hover:bg-white/10 text-blue-200 hover:text-white transition-colors shadow-sm"
                        >
                            <span className="font-semibold">Participate</span>
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>
                    <p className="text-xl text-blue-200 max-w-2xl mx-auto leading-relaxed mb-8">
                        A proposal for a different kind of legacy in space.
                    </p>
                    <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed mb-6">
                        Space is no longer abstract. We launch things every week. But almost everything we send out there is designed for utility: communication, observation, defense.
                    </p>
                    <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        Still, every now and then, someone adds something else... an artwork, a song, a symbol. It's not part of the mission plan. But it keeps happening.
                    </p>
                </div>

                {/* Mission statement */}
                <div className="text-center mb-24">
                    <h2 className="text-3xl mb-8 text-blue-400 font-bold">
                        Space Blues exists to take that idea seriously.
                    </h2>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        That when we go out into the solar system, we should bring our culture with us. Not just data or infrastructure—but signs of life, creativity, and care.
                    </p>
                </div>

                {/* What We're Exploring section */}
                <div className="mb-24">
                    <h2 className="text-3xl mb-8 text-blue-400 text-center">What We're Exploring</h2>
                    <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed text-center mb-12">
                        Space Blues is an evolving project. We're not launching anything yet. Right now, we're building the foundation: connecting with artists, engineers, space agencies, and public institutions. We're asking: what would it take to make this a real, repeatable part of how we explore space?
                    </p>

                    {/* Future directions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {sections.map((section, index) => (
                            <div
                                key={index}
                                className={`p-6 rounded-lg transition-all duration-500 cursor-pointer ${
                                    activeSection === index
                                        ? 'bg-white/10 backdrop-blur-lg transform -translate-x-2'
                                        : 'hover:bg-white/5'
                                }`}
                                onClick={() => setActiveSection(index)}
                            >
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="text-blue-400">{section.icon}</div>
                                    <h3 className="text-xl font-bold">{section.title}</h3>
                                </div>
                                <p className="text-gray-300 leading-relaxed">{section.content}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Interactive visualization */}
                <div className="mb-24">
                    <div className="relative h-96 rounded-lg overflow-hidden backdrop-blur-lg bg-white/5">
                        <div className="absolute inset-0 flex items-center justify-center">
                            {/* Central pulsing elements */}
                            <div className="w-32 h-32 rounded-full bg-blue-500/20 animate-pulse" />
                            <div className="absolute w-24 h-24 rounded-full bg-purple-500/20 animate-ping" />
                            <div className="absolute w-16 h-16 rounded-full bg-white/20 animate-pulse" />

                            {/* Trail effect on inner orbit */}
                            {[...Array(5)].map((_, i) => {
                                const size = 12 - i * 1.5;
                                return (
                                    <div
                                        key={i}
                                        className="absolute bg-blue-400 rounded-full shadow-blue-400/30"
                                        style={{
                                            width: `${size}px`,
                                            height: `${size}px`,
                                            animation: 'orbit-inner 10s linear infinite',
                                            animationDelay: `${i * 0.2}s`,
                                            opacity: 1 - i * 0.3,
                                            transformOrigin: 'center',
                                        }}
                                    ></div>
                                );
                            })}

                            {/* Orbiting planet on outer orbit */}
                            <div
                                className="absolute w-4 h-4 bg-blue-400 rounded-full shadow-lg shadow-blue-400/50"
                                style={{
                                    animation: 'orbit-inner 10s linear infinite',
                                    transformOrigin: 'center',
                                }}
                            ></div>

                            {/* Orbiting star on a separate orbit */}
                            <div
                                className="absolute w-4 h-4 bg-yellow-400 rounded-full shadow-lg shadow-yellow-400/50"
                                style={{
                                    animation: 'orbit-separate 7s linear infinite',
                                    transformOrigin: 'center',
                                    animationDelay: '-2s',
                                }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Why It Matters section */}
                <div className="mb-24">
                    <h2 className="text-3xl mb-8 text-blue-400 text-center">Why It Matters</h2>
                    <div className="max-w-3xl mx-auto space-y-6">
                        <p className="text-gray-300 leading-relaxed">
                            If we disappear, what will be left? Right now, the answer is: infrastructure. Code. Extractive systems.
                        </p>
                        <p className="text-gray-300 leading-relaxed">
                            We believe we can leave something better. Not because we're perfect, but because we tried. To create. To express. To be remembered for more than what we built.
                        </p>
                        <p className="text-gray-300 leading-relaxed">
                            This isn't about spectacle. It's about responsibility. It's about treating space as something more than real estate.
                        </p>
                    </div>
                </div>

                {/* Final proposition */}
                <div className="text-center max-w-3xl mx-auto mb-24">
                    <h2 className="text-3xl mb-8 text-blue-400 font-bold">
                        Space Blues is a quiet proposal:
                    </h2>
                    <p className="text-xl text-gray-300 leading-relaxed">
                        Let's make the human presence in space more human. Let's leave traces worth finding.
                    </p>
                </div>

                <footer className="text-center text-blue-400 mt-8">© Space Blues Co.</footer>
            </div>

            <style jsx>{`
                @keyframes orbit-inner {
                    from {
                        transform: rotate(0deg) translateX(50px) rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg) translateX(50px) rotate(-360deg);
                    }
                }

                @keyframes orbit-outer {
                    from {
                        transform: rotate(0deg) translateX(80px) rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg) translateX(80px) rotate(-360deg);
                    }
                }

                @keyframes orbit-separate {
                    from {
                        transform: rotate(0deg) translateX(110px) rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg) translateX(110px) rotate(-360deg);
                    }
                }

                @keyframes starMove {
                    0% {
                        transform: translate(0, 0);
                    }
                    100% {
                        transform: translate(-100px, 100px);
                    }
                }
            `}</style>
        </div>
    );
};

export default SpaceBluesPage;